import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useToast } from "@/components/toast";
import { Button, Checkbox, Field, SectionLabel, SelectField, Toggle } from "@/components/ui";
import { SelectDialog } from "@/components/select-dialog";
import { AuthShell } from "@/components/auth-shell";
import { passwordOk } from "@/lib/format";

const initial = {
  full_name: "", display_name: "", email: "", phone: "", whatsapp: "", password: "", confirm_password: "",
  day: "", month: "", year: "", country: "", city: "", accept_terms: false, accept_privacy: false, whatsapp_consent: true, invite_code: "",
};

function ageOf(y, m, d) {
  const t = new Date();
  let a = t.getFullYear() - y;
  if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) a -= 1;
  return a;
}

function validate(f) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Enter your full name";
  if (f.display_name.trim().length < 2) e.display_name = "Choose a display name";
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) e.email = "Enter a valid email";
  if (!/^\+?[\d\s-]{7,18}$/.test(f.phone.trim())) e.phone = "Enter a valid phone number";
  if (!/^\+[1-9][\d\s-]{7,18}$/.test(f.whatsapp.trim())) e.whatsapp = "Include country code, e.g. +2348012345678";
  if (!passwordOk(f.password)) e.password = "At least 8 characters with letters and numbers";
  if (f.password !== f.confirm_password) e.confirm_password = "Passwords do not match";
  const d = parseInt(f.day, 10), m = parseInt(f.month, 10), y = parseInt(f.year, 10);
  const dt = new Date(y, m - 1, d);
  if (!d || !m || !y || f.year.length !== 4 || dt.getMonth() !== m - 1 || dt.getDate() !== d) e.dob = "Enter a valid date of birth";
  else if (ageOf(y, m, d) < 18) e.dob = "You must be at least 18 years old to join LOVE NET.";
  if (!f.country) e.country = "Select your country";
  if (f.city.trim().length < 2) e.city = "Enter your city or state";
  if (!f.accept_terms) e.accept_terms = "Required";
  if (!f.accept_privacy) e.accept_privacy = "Required";
  return e;
}

export default function Register() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const invite = params.get("invite") ?? "";
  const [f, setF] = useState({ ...initial, invite_code: invite.toUpperCase() });
  const [invitePreview, setInvitePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const { data: options } = useQuery({ queryKey: ["options"], queryFn: () => api("/options", { auth: false }) });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const checkInvite = async (raw) => {
    const code = raw.trim().toUpperCase();
    if (!code) return setInvitePreview(null);
    try { setInvitePreview(await api(`/invites/${encodeURIComponent(code)}`, { auth: false })); } catch { setInvitePreview(null); }
  };
  useEffect(() => { if (invite) checkInvite(invite); }, [invite]);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) return toast("Please fix the highlighted fields", "error");
    setLoading(true);
    try {
      const dob = `${f.year}-${f.month.padStart(2, "0")}-${f.day.padStart(2, "0")}`;
      const res = await api("/auth/register", {
        method: "POST", auth: false,
        body: {
          full_name: f.full_name, display_name: f.display_name, email: f.email.trim(), phone: f.phone, whatsapp: f.whatsapp,
          password: f.password, confirm_password: f.confirm_password, dob, country: f.country, city: f.city,
          accept_terms: f.accept_terms, accept_privacy: f.accept_privacy, whatsapp_consent: f.whatsapp_consent, invite_code: f.invite_code.trim() || null,
        },
      });
      if (res.invite_status === "not_recognized") toast("Invite code not recognized — your account was still created.", "info");
      else if (res.group_name) toast(`Joined via ${res.group_name}`, "success");
      navigate(`/verify?email=${encodeURIComponent(res.email)}`, { replace: true });
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const inviteHint = invitePreview ? (invitePreview.valid ? `You're joining with ${invitePreview.group_name}` : "Invite code not recognized — you can still sign up") : "From your community group, if you have one";

  return (
    <AuthShell step="Step 1 of 5 · Account" backTestId="register-back-button" onBack={() => navigate("/landing")}>
      <h1 className="text-[34px] leading-tight">Create your account</h1>
      <p className="mb-6 mt-1 text-sm leading-5 text-muted-foreground">Your details stay private. Only your display name, age and city appear on your profile.</p>
      <form onSubmit={submit} noValidate data-testid="register-form">
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Field testId="register-full-name-input" label="Full name" value={f.full_name} onChange={set("full_name")} error={errors.full_name} autoComplete="name" placeholder="Amara Okafor" />
          <Field testId="register-display-name-input" label="Display name" value={f.display_name} onChange={set("display_name")} error={errors.display_name} placeholder="Amara" hint="Shown to other members" />
        </div>
        <Field testId="register-email-input" label="Email" type="email" value={f.email} onChange={set("email")} error={errors.email} autoComplete="email" placeholder="you@example.com" />
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Field testId="register-phone-input" label="Mobile phone" type="tel" value={f.phone} onChange={set("phone")} error={errors.phone} placeholder="+234 801 234 5678" />
          <Field testId="register-whatsapp-input" label="WhatsApp (with country code)" type="tel" autoComplete="off" value={f.whatsapp} onChange={set("whatsapp")} error={errors.whatsapp} placeholder="+234 801 234 5678" hint="Encrypted and never shown publicly" />
          <Field testId="register-password-input" label="Password" type="password" autoComplete="new-password" value={f.password} onChange={set("password")} error={errors.password} placeholder="At least 8 characters" />
          <Field testId="register-confirm-password-input" label="Confirm password" type="password" autoComplete="new-password" value={f.confirm_password} onChange={set("confirm_password")} error={errors.confirm_password} placeholder="Re-enter password" />
        </div>
        <p className="mb-2 text-[15px] font-bold">Date of birth</p>
        <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-3">
          <Field testId="register-dob-day-input" label="Day" inputMode="numeric" maxLength={2} value={f.day} onChange={set("day")} placeholder="DD" className="mb-0" />
          <Field testId="register-dob-month-input" label="Month" inputMode="numeric" maxLength={2} value={f.month} onChange={set("month")} placeholder="MM" className="mb-0" />
          <Field testId="register-dob-year-input" label="Year" inputMode="numeric" maxLength={4} value={f.year} onChange={set("year")} placeholder="YYYY" className="mb-0" />
        </div>
        {errors.dob ? <p className="mt-1 text-xs font-medium text-[#9E3C3E]" data-testid="register-dob-error">{errors.dob}</p> : null}
        <div className="mt-4 grid gap-x-4 sm:grid-cols-2">
          <SelectField testId="register-country-select" label="Country of residence" value={f.country} placeholder="Select country" onClick={() => setCountryOpen(true)} error={errors.country} />
          <Field testId="register-city-input" label="City / State" value={f.city} onChange={set("city")} error={errors.city} placeholder="Lagos" />
        </div>
        <Field testId="register-invite-code-input" label="Invite code (optional)" value={f.invite_code} placeholder="e.g. GRACE24" hint={inviteHint}
          onChange={(e) => { set("invite_code")(e.target.value.toUpperCase()); setInvitePreview(null); }} onBlur={() => checkInvite(f.invite_code)} />
        <SectionLabel>WhatsApp sharing</SectionLabel>
        <div className="mb-4 flex items-center gap-4 rounded-xl bg-[#F6E3E4] p-4">
          <div className="flex-1">
            <p className="text-sm font-bold">Share my WhatsApp after mutual acceptance</p>
            <p className="mt-1 text-xs leading-[17px]">Your number is encrypted and never shown on your profile. It is revealed only to members whose request you accept (or who accept yours). You can change this anytime.</p>
          </div>
          <Toggle testId="register-whatsapp-consent-switch" label="Share my WhatsApp after mutual acceptance" checked={f.whatsapp_consent} onChange={set("whatsapp_consent")} />
        </div>
        <Checkbox testId="register-terms-checkbox" checked={f.accept_terms} error={errors.accept_terms} onToggle={() => set("accept_terms")(!f.accept_terms)}>
          I am 18+ and agree to the LOVE NET <Link to="/terms" target="_blank" data-testid="register-terms-link" className="font-bold text-primary underline" onClick={(e) => e.stopPropagation()}>Terms and Conditions</Link>.
        </Checkbox>
        <Checkbox testId="register-privacy-checkbox" checked={f.accept_privacy} error={errors.accept_privacy} onToggle={() => set("accept_privacy")(!f.accept_privacy)}>
          I consent to LOVE NET processing my data as described in the <Link to="/privacy" target="_blank" data-testid="register-privacy-link" className="font-bold text-primary underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>.
        </Checkbox>
        <Button type="submit" testId="register-submit-button" title="Create account" loading={loading} className="mt-4 w-full" />
      </form>
      <button data-testid="register-login-link" onClick={() => navigate("/login", { replace: true })} className="mt-3 w-full py-3 text-sm text-muted-foreground">
        Already a member? <span className="font-bold text-primary">Log in</span>
      </button>
      <SelectDialog testId="register-country-sheet" open={countryOpen} onClose={() => setCountryOpen(false)} title="Country of residence"
        options={options?.countries ?? []} value={f.country ? [f.country] : []} onChange={(v) => set("country")(v[0] ?? "")} />
    </AuthShell>
  );
}
